import { useBreakpoints } from '@/hooks/useIsMobile';
import { usePersistentState } from '@/utils/hooks';
import CompressOutlined from '@ant-design/icons/CompressOutlined';
import ExpandOutlined from '@ant-design/icons/ExpandOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import Breadcrumb from 'antd/es/breadcrumb';
import Button, { type ButtonProps } from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import { Link } from 'react-router-dom';
import { css, styled, useTheme } from 'styled-components';

export interface ActionConfig {
  label: string;
  type?: ButtonProps['type'] | 'secondary' | 'danger';
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  divided?: boolean;
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: ActionConfig[];
  breadcrumbs?: BreadcrumbItem[];
  showFullScreenToggle?: boolean;
  fullScreen?: boolean;
  onFullScreenToggle?: (fullScreen: boolean) => void;
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.marginXS}px;

    .actions-buttons {
      display: flex;
      gap: ${theme.marginXS}px;
    }
    .actions-dropdown {
      display: none;
    }

    @container main-content (max-width: ${theme.screenMD}px) {
      .actions-buttons {
        display: none;
      }
      .actions-dropdown {
        display: block;
      }
    }
  `}
`;

const BreadcrumbContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.marginXS}px;
  `}
`;

const FullScreenOnlyContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    margin-bottom: ${theme.marginXS}px;
  `}
`;

export const PageHeader = ({
  title,
  description,
  actions,
  breadcrumbs,
  showFullScreenToggle = false,
  fullScreen: controlledFullScreen,
  onFullScreenToggle,
}: PageHeaderProps) => {
  const theme = useTheme();
  const { isExtraExtraLarge } = useBreakpoints();
  const [internalFullScreen, setInternalFullScreen] = usePersistentState(
    'fullScreen',
    false,
  );

  // Use controlled fullScreen if provided, otherwise use internal state
  const fullScreen = controlledFullScreen ?? internalFullScreen;
  const setFullScreen = onFullScreenToggle ?? setInternalFullScreen;

  const showFullscreenButton = showFullScreenToggle && isExtraExtraLarge;

  const renderBreadcrumbs = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) {
      // If no breadcrumbs but we have a fullscreen button, show it on its own row
      if (showFullscreenButton) {
        return (
          <FullScreenOnlyContainer>
            <Button
              type="text"
              icon={fullScreen ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setFullScreen(!fullScreen)}
            />
          </FullScreenOnlyContainer>
        );
      }
      return null;
    }

    const breadcrumbItems = breadcrumbs.map((item, index) => ({
      key: index,
      title: item.path ? (
        <Link to={item.path}>{item.label}</Link>
      ) : (
        <span>{item.label}</span>
      ),
    }));

    return (
      <BreadcrumbContainer>
        <Breadcrumb items={breadcrumbItems} />
        {showFullscreenButton && (
          <Button
            type="text"
            icon={fullScreen ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setFullScreen(!fullScreen)}
          />
        )}
      </BreadcrumbContainer>
    );
  };

  const renderActions = () => {
    if (!actions || actions.length === 0) return null;

    // If 1-3 actions, show all as buttons
    if (actions.length <= 3) {
      return (
        <ActionsContainer>
          <div className="actions-buttons">
            {actions.map((action, index) => (
              <Button
                key={index}
                type={
                  action.type === 'secondary'
                    ? undefined
                    : (action.type as never)
                }
                icon={action.icon}
                onClick={action.onClick}
                danger={action.danger}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          </div>
          <div className="actions-dropdown">
            <Dropdown
              menu={{
                items: (() => {
                  const nonDividedActions = actions.filter(
                    (action) => !action.divided,
                  );
                  const dividedActions = actions.filter(
                    (action) => action.divided,
                  );

                  return [
                    ...nonDividedActions.map((action, index) => ({
                      key: `non-divided-${index}`,
                      label: action.label,
                      icon: action.icon,
                      danger: action.danger,
                      disabled: action.disabled,
                      onClick: action.onClick,
                    })),
                    ...(dividedActions.length > 0 &&
                    nonDividedActions.length > 0
                      ? [{ type: 'divider' as const, key: 'divider' }]
                      : []),
                    ...dividedActions.map((action, index) => ({
                      key: `divided-${index}`,
                      label: action.label,
                      icon: action.icon,
                      danger: action.danger,
                      disabled: action.disabled,
                      onClick: action.onClick,
                    })),
                  ];
                })(),
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button icon={<MoreOutlined />}>Actions</Button>
            </Dropdown>
          </div>
        </ActionsContainer>
      );
    }

    // If 4+ actions, show buttons and rest in dropdown (ensure at least 2 items in dropdown)
    const buttonCount = actions.length === 4 ? 2 : 3;
    const buttonActions = actions.slice(0, buttonCount);
    const remainingActions = actions.slice(buttonCount);

    // Separate remaining actions into divided and non-divided
    const nonDividedActions = remainingActions.filter(
      (action) => !action.divided,
    );
    const dividedActions = remainingActions.filter((action) => action.divided);

    const dropdownItems = [
      ...nonDividedActions.map((action, index) => ({
        key: `non-divided-${index}`,
        label: action.label,
        icon: action.icon,
        danger: action.danger,
        disabled: action.disabled,
        onClick: action.onClick,
      })),
      ...(dividedActions.length > 0 && nonDividedActions.length > 0
        ? [{ type: 'divider' as const, key: 'divider' }]
        : []),
      ...dividedActions.map((action, index) => ({
        key: `divided-${index}`,
        label: action.label,
        icon: action.icon,
        danger: action.danger,
        disabled: action.disabled,
        onClick: action.onClick,
      })),
    ];

    return (
      <ActionsContainer>
        <div className="actions-buttons">
          {buttonActions.map((action, index) => (
            <Button
              key={index}
              type={
                action.type === 'secondary' ? undefined : (action.type as never)
              }
              icon={action.icon}
              onClick={action.onClick}
              danger={action.danger}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          ))}
          {dropdownItems.length > 0 && (
            <Dropdown
              menu={{ items: dropdownItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button icon={<MoreOutlined />}>More</Button>
            </Dropdown>
          )}
        </div>
        <div className="actions-dropdown">
          <Dropdown
            menu={{
              items: (() => {
                const nonDividedActions = actions.filter(
                  (action) => !action.divided,
                );
                const dividedActions = actions.filter(
                  (action) => action.divided,
                );

                return [
                  ...nonDividedActions.map((action, index) => ({
                    key: `non-divided-${index}`,
                    label: action.label,
                    icon: action.icon,
                    danger: action.danger,
                    disabled: action.disabled,
                    onClick: action.onClick,
                  })),
                  ...(dividedActions.length > 0 && nonDividedActions.length > 0
                    ? [{ type: 'divider' as const, key: 'divider' }]
                    : []),
                  ...dividedActions.map((action, index) => ({
                    key: `divided-${index}`,
                    label: action.label,
                    icon: action.icon,
                    danger: action.danger,
                    disabled: action.disabled,
                    onClick: action.onClick,
                  })),
                ];
              })(),
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button icon={<MoreOutlined />}>Actions</Button>
          </Dropdown>
        </div>
      </ActionsContainer>
    );
  };

  return (
    <>
      {renderBreadcrumbs()}
      <HeaderContainer>
        <div>
          <Title
            level={2}
            style={{ marginBlock: theme.marginMD, marginRight: theme.marginXS }}
          >
            {title}
          </Title>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.marginXS,
          }}
        >
          {renderActions()}
        </div>
      </HeaderContainer>
      {description && <Paragraph>{description}</Paragraph>}
    </>
  );
};
